import { FC, useCallback, useEffect } from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { ProductFormData, YesOrNo } from '@src/types';
import { Button, Input, RadioGroup, RadioGroupItem } from '@features/ui';
import { Plus } from 'lucide-react';
import { isBundleData } from '@features/admin/productsAddons/productsAddonsData.ts';

export const ProductPayoutLogicForm: FC<UseFormReturn<ProductFormData>> = ({
  control,
  setValue,
  watch,
  formState: { errors },
}) => {
  const { register } = useFormContext<ProductFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'payout_logics',
  });

  const payoutLogicNumber = useWatch({
    control,
    name: 'max_payouts_per_account',
  });

  const payoutLogicsAccount = watch('payout_logics');

  useEffect(() => {
    const count = payoutLogicNumber || 0;

    if (count < fields.length) {
      remove([...Array(fields.length - count).keys()].reverse());
    } else if (count > fields.length) {
      // @ts-ignore
      append(Array.from({ length: count - fields.length }, () => ({})));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutLogicNumber]);

  const renderArrayFormItem = useCallback(
    (field: any, index: number) => {
      const isLast = fields.length - 1 === index;

      const dailyProfitRequired =
        payoutLogicsAccount?.[index]?.daily_profit_required;

      const applyToCopyAccount =
        payoutLogicsAccount?.[index]?.apply_to_copy_account;

      return (
        <div key={`payout_logics_${field?.id}_${index}`}>
          <div
            className={
              'flex lg:flex-row flex-col w-full gap-x-[30px] gap-y-7 lg:mt-[30px] mt-7'
            }
          >
            <div className={'flex flex-col flex-1 gap-[14px]'}>
              <p>Min Payout $</p>
              <Input
                {...register(`payout_logics.${index}.min_payout`)}
                error={!!errors.payout_logics?.[index]?.min_payout?.message}
                placeholder={'Enter'}
                textSize="xs"
                type="number"
                variant="secondary"
              />
            </div>
            <div className={'flex flex-col flex-1 gap-[14px]'}>
              <p>Max Payout $</p>
              <Input
                {...register(`payout_logics.${index}.max_payout`)}
                error={!!errors.payout_logics?.[index]?.max_payout?.message}
                placeholder={'Enter'}
                textSize="xs"
                type="number"
                variant="secondary"
              />
            </div>
            <div className={'lg:flex hidden flex-1'} />
            <div className={'xl:flex hidden flex-1'} />
          </div>
          <div
            className={
              'flex lg:flex-row flex-col lg:w-[100%] w-full gap-x-[30px] gap-y-7 lg:mt-[45px] mt-7'
            }
          >
            <div className={'flex flex-col flex-1 gap-[14px]'}>
              <p>Request Payout after</p>
              <Input
                {...register(
                  `payout_logics.${index}.payout_request_after_days`
                )}
                error={
                  !!errors.payout_logics?.[index]?.payout_request_after_days
                    ?.message
                }
                placeholder={'Enter Days'}
                textSize="xs"
                type="number"
                variant="secondary"
              />
            </div>
            <div className={'flex flex-col flex-1 gap-[14px]'}>
              <p>When does trader receive reduced percentage of sim profits</p>
              <Input
                {...register(`payout_logics.${index}.split_start_amount`)}
                error={
                  !!errors.payout_logics?.[index]?.split_start_amount?.message
                }
                placeholder={'$'}
                textSize="xs"
                type="number"
                variant="secondary"
              />
            </div>
            <div className={'flex flex-col flex-1 gap-[14px]'}>
              <p>Trader percentage of sim profits</p>
              <Input
                {...register(`payout_logics.${index}.split_percentage`)}
                error={
                  !!errors.payout_logics?.[index]?.split_percentage?.message
                }
                placeholder={'Enter %'}
                textSize="xs"
                type="number"
                variant="secondary"
              />
            </div>
            <div className={'xl:flex hidden flex-1'} />
          </div>
          <div
            className={
              'flex lg:flex-row flex-col w-full gap-x-[30px] gap-y-7 lg:mt-[45px] mt-7'
            }
          >
            <Controller
              name={`payout_logics.${index}.apply_to_copy_account`}
              render={({ field }) => (
                <div>
                  <div className={'lg:h-[32px]'} />
                  <div
                    className={
                      'flex lg:gap-[31px] gap-4 lg:flex-row flex-col lg:items-center min-h-[38px]'
                    }
                  >
                    <p className={'w-[201px]'}>
                      Amount applied to separate PA
                    </p>
                    <RadioGroup
                      className={'flex gap-x-[18px]'}
                      value={
                        typeof field.value === 'boolean'
                          ? field.value
                            ? YesOrNo.Yes
                            : YesOrNo.No
                          : ''
                      }
                      onValueChange={(value) => {
                        field.onChange(value === YesOrNo.Yes);
                        setValue(
                          `payout_logics.${index}.copy_account_amount`,
                          undefined
                        );
                      }}
                    >
                      {isBundleData.map((item) => (
                        <div
                          key={item.value}
                          className={'flex items-center gap-x-[18px]'}
                        >
                          <RadioGroupItem value={item.value} />
                          <p>{item.label}</p>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                  <p className={'text-red-500 lg:mt-0 mt-1.5'}>
                    {
                      errors.payout_logics?.[index]?.apply_to_copy_account
                        ?.message
                    }
                  </p>
                </div>
              )}
            />
            <div className={'flex flex-col gap-[14px]'}>
              <p>How much $</p>
              <Input
                {...register(`payout_logics.${index}.copy_account_amount`)}
                disabled={!applyToCopyAccount}
                error={
                  !!errors.payout_logics?.[index]?.copy_account_amount?.message
                }
                placeholder={'$'}
                textSize="xs"
                type="number"
                variant="secondary"
              />
            </div>
            <div className={'xl:flex hidden flex-1'} />
          </div>
          <div
            className={
              'flex lg:flex-row flex-col lg:w-[100%] w-full gap-[30px] mt-[45px]'
            }
          >
            <Controller
              name={`payout_logics.${index}.daily_profit_required`}
              render={({ field }) => (
                <div>
                  <div className={'lg:flex hidden h-[32px]'} />
                  <div
                    className={
                      'flex lg:gap-[31px] gap-4 lg:flex-row flex-col lg:items-center min-h-[38px]'
                    }
                  >
                    <p className={'w-[201px]'}>Daily Profit Required</p>
                    <RadioGroup
                      className={'flex gap-x-[18px]'}
                      value={
                        typeof field.value === 'boolean'
                          ? field.value
                            ? YesOrNo.Yes
                            : YesOrNo.No
                          : ''
                      }
                      onValueChange={(value) => {
                        field.onChange(value === YesOrNo.Yes);
                        setValue(
                          `payout_logics.${index}.daily_profit_required_amount`,
                          undefined
                        );
                      }}
                    >
                      {isBundleData.map((item) => (
                        <div
                          key={item.value}
                          className={'flex items-center gap-x-[18px]'}
                        >
                          <RadioGroupItem value={item.value} />
                          <p>{item.label}</p>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                  <p className={'text-red-500 lg:mt-0 mt-1.5'}>
                    {
                      errors.payout_logics?.[index]?.daily_profit_required
                        ?.message
                    }
                  </p>
                </div>
              )}
            />
            <div className={'flex flex-col gap-[14px]'}>
              <p>How much $</p>
              <Input
                {...register(
                  `payout_logics.${index}.daily_profit_required_amount`
                )}
                disabled={!dailyProfitRequired}
                error={
                  !!errors.payout_logics?.[index]?.daily_profit_required_amount
                    ?.message
                }
                placeholder={'$'}
                textSize="xs"
                type="number"
                variant="secondary"
              />
            </div>
            <div className={'flex flex-col gap-[14px]'}>
              <p>Required Balance Threshold</p>
              <Input
                {...register(
                  `payout_logics.${index}.required_balance_threshold_max`
                )}
                error={
                  !!errors.payout_logics?.[index]
                    ?.required_balance_threshold_max?.message
                }
                placeholder={'$'}
                textSize="xs"
                type="number"
                variant="secondary"
              />
            </div>
          </div>
          {!isLast && (
            <div
              className={
                'border-dotted border-[#FFFFFF] border-t-[1.5px] w-full pl-[6px] mt-[38px]'
              }
            />
          )}
        </div>
      );
    },
    [
      errors.payout_logics,
      fields.length,
      payoutLogicsAccount,
      register,
      setValue,
    ]
  );

  return (
    <div
      className={
        'text-xs mb-[33px] border-dotted border-[#FFFFFF] border-t-[1.5px]'
      }
    >
      <div className={'mt-[9px] lg:ml-10 ml-5'}>
        <div className={'w-fit flex flex-col gap-[5px]'}>
          <p className={'text-[16px]'}>Payout Logic</p>
          <div className={'w-full h-[1px] bg-white'} />
        </div>
        <div
          className={
            'w-full border border-white pr-[28px] pl-[11px] pt-[15px] pb-[20px] mt-[20px]'
          }
        >
          <div
            className={
              'flex lg:flex-row flex-col lg:w-[100%] w-full gap-x-[30px] gap-y-7'
            }
          >
            <Controller
              control={control}
              name="max_payouts_per_account"
              render={({ field }) => (
                <div className={'flex flex-col flex-1 gap-[14px]'}>
                  <p>How many payouts can this account have</p>
                  <Input
                    endIcon={
                      <Button
                        aria-label="Add"
                        className="h-[17px] w-[17px] rounded-full bg-white text-black hover:bg-[none]"
                        size={'icon'}
                        type="button"
                        onClick={() => {
                          const current = parseInt(
                            field.value?.toString() || '0',
                            10
                          );

                          field.onChange(current + 1);
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    }
                    error={!!errors.max_payouts_per_account?.message}
                    inputMode="numeric"
                    min={0}
                    pattern="\d*"
                    placeholder="0"
                    step={1}
                    textSize="xs"
                    type="number"
                    value={field.value}
                    variant="secondary"
                    onChange={(e) => {
                      const value = e.target.value;

                      if (/^\d*$/.test(value)) {
                        field.onChange(value);
                      }
                    }}
                    onKeyDown={(e) => {
                      const invalidKeys = ['e', 'E', '+', '-', '.'];

                      if (invalidKeys.includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              )}
            />
            <div className={'lg:flex hidden flex-1'} />
            <div className={'lg:flex hidden flex-1'} />
            <div className={'lg:flex hidden flex-1'} />
          </div>
          {fields.map((field, index) => renderArrayFormItem(field, index))}
        </div>
      </div>
    </div>
  );
};
